import axios, { AxiosInstance } from 'axios'

// ✅ 统一从环境变量读取API地址
// 开发环境使用空字符串，让请求走Vite代理（Vite会自动转发到配置的后端地址）
// 生产环境使用环境变量或空字符串（使用相对路径）
const API_BASE_URL = import.meta.env.DEV ? '' : (import.meta.env.VITE_API_BASE_URL || '')

/**
 * 创建API客户端实例
 */
export function createApiClient(baseURL: string): AxiosInstance {
  const client = axios.create({
    baseURL,
    timeout: 300000, // 超时时间5分钟（导入预览等耗时操作）
    headers: {
      'Content-Type': 'application/json',
    },
    paramsSerializer: {
      indexes: null, // 使用重复参数格式: role=ADMIN&role=USER 而不是 role[]=ADMIN&role[]=USER
      serialize: (params: any) => {
        // ✅ 自定义序列化，避免双重编码
        const parts: string[] = []
        Object.keys(params).forEach(key => {
          const value = params[key]
          if (value === null || value === undefined) return
          
          if (Array.isArray(value)) {
            value.forEach(item => {
                parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(item)}`)
              })
          } else {
              {
                parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
              }
          }
        })
        return parts.join('&')
      },
    },
  })

  // 请求拦截器：添加认证token
  client.interceptors.request.use(
    (config) => {
      // 🔥 开发模式下强制使用相对路径，确保通过 Vite 代理
      if (import.meta.env.DEV && config.url?.startsWith('/api')) {
        // 强制移除 baseURL，确保使用相对路径
        config.baseURL = ''
      }

      // 从localStorage获取token并添加到请求头
      const token = localStorage.getItem('authToken')
      if (token) {
        config.headers.Authorization = `Bearer ${token}`
      }

      return config
    },
    (error) => {
      console.error('[API Request Error]', error)
      return Promise.reject(error)
    }
  )

  // 响应拦截器：滑动续期
  client.interceptors.response.use(
    (response) => {
      // 滑动续期：后端在剩余<30分钟时返回新 token，前端替换本地 token
      const refreshedToken = response.headers['x-refreshed-token']
      if (refreshedToken) {
        localStorage.setItem('authToken', refreshedToken)
      }
      return response
    },
    (error) => {
      // ✅ 忽略取消请求的错误日志 (AbortController 或 axios cancel)
      const isCancelError =
        error.code === 'ERR_CANCELED' ||
        error.name === 'AbortError' ||
        error.name === 'CanceledError' ||
        error.message === 'canceled'

      if (!isCancelError) {
        // ✅ 只记录真正的错误
        console.error('[API Response Error]', {
          message: error.message,
          code: error.code,
          url: error.config?.url,
          baseURL: error.config?.baseURL,
        })
      }
      return Promise.reject(error)
    }
  )

  return client
}

/**
 * 默认API客户端
 */
export const apiClient = createApiClient(API_BASE_URL)
